import { cn } from '@/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { HTMLAttributes, PropsWithChildren } from 'react';

/*
 * A full-bleed horizontal band of page.
 *
 * The public site reads as an alternating stack of warm surfaces, occasionally
 * going dark. Bands paint that rhythm; SectionLayout constrains the content
 * inside them.
 *
 * `pattern` fills a band with one of the craft motifs from app.css instead of
 * leaving it flat — the first pass left every section a near-white field, which
 * is what made the site read as empty.
 */
const bandVariants = cva('relative w-full', {
  variants: {
    tone: {
      /** The page's own warm surface. */
      surface: 'bg-surface text-foreground',
      /** The deepest light step — use where a band needs to hold its own. */
      deep: 'bg-surface-deep text-foreground',
      /** Warm ivory, for the primary reading bands. */
      raised: 'bg-background text-foreground',
      /** Warm cream carrying a hint of the brand orange. */
      cream: 'bg-primary-muted text-foreground',
      /** The logo's black. Used as a surface, not just as text. */
      ink: 'bg-ink text-ink-foreground',
      /** The one non-orange accent, for a band that needs to break the warmth. */
      indigo: 'bg-indigo text-indigo-foreground',
    },
  },
  defaultVariants: {
    tone: 'raised',
  },
});

const patterns = {
  stitch: 'pattern-stitch',
  lattice: 'pattern-lattice',
  weave: 'pattern-weave',
} as const;

type BandProps = PropsWithChildren<HTMLAttributes<HTMLDivElement>> &
  VariantProps<typeof bandVariants> & {
    /** Draw the seam rule along the band's top edge. */
    seam?: boolean;
    /** Fill the band with a craft motif. */
    pattern?: keyof typeof patterns;
    /** How strongly the pattern reads. Defaults to a background whisper. */
    patternOpacity?: 'subtle' | 'medium' | 'strong';
  };

const patternOpacities = {
  subtle: 'opacity-40',
  medium: 'opacity-70',
  strong: 'opacity-100',
};

const Band = ({
  children,
  className,
  tone,
  seam = false,
  pattern,
  patternOpacity = 'subtle',
  ...props
}: BandProps) => {
  const dark = tone === 'ink' || tone === 'indigo';

  return (
    <div className={cn(bandVariants({ tone }), className)} {...props}>
      {pattern && (
        <div
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-0',
            patterns[pattern],
            patternOpacities[patternOpacity],
            dark && 'pattern-on-ink'
          )}
        />
      )}

      {seam && (
        <div
          aria-hidden
          className={cn(
            'seam relative mx-auto max-w-7xl',
            dark && 'seam-on-ink'
          )}
        />
      )}

      {/* Above the pattern layer. */}
      <div className="relative">{children}</div>
    </div>
  );
};

export default Band;
